import React from 'react';
import { Link } from 'react-router-dom';
import { Zap } from 'lucide-react';

const EFFECTIVE_DATE = '4 March 2026';
const COMPANY = 'Best Business Development';
const CONTACT_EMAIL = 'info@bbdoutreachengine.com';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-bold text-gray-900 mb-4">{title}</h2>
    <div className="space-y-3 text-gray-600 leading-relaxed text-sm">{children}</div>
  </div>
);

const PrivacyPage = () => (
  <div className="min-h-screen bg-white" style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>

    {/* NAV */}
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-md shadow-indigo-200">
              <Zap size={16} className="text-white" />
            </div>
            <span className="text-lg font-bold text-gray-900 tracking-tight">Outreach Engine</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">Sign In</Link>
            <Link to="/pricing" className="bg-indigo-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
              Get Started
            </Link>
          </div>
        </div>
      </div>
    </header>

    {/* CONTENT */}
    <main className="max-w-3xl mx-auto px-6 py-16">
      <div className="mb-12">
        <p className="text-indigo-600 text-xs font-bold uppercase tracking-widest mb-3">Legal</p>
        <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight mb-3">Privacy Policy</h1>
        <p className="text-sm text-gray-400">Effective date: {EFFECTIVE_DATE}</p>
      </div>

      <p className="text-gray-600 text-sm leading-relaxed mb-10">
        This Privacy Policy explains how <strong>{COMPANY}</strong> ("we", "us", or "our"), the operator of
        Outreach Engine, collects, uses, and protects your personal data. We are a UK-based company and this
        policy is written in compliance with the UK GDPR and the Data Protection Act 2018.
        We act as the <strong>data controller</strong> for account and billing data described in this policy.
      </p>

      <Section title="1. Data We Collect">
        <p>We collect the following categories of personal data:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>Account data:</strong> your name and email address, collected when you purchase a
            subscription and complete your account setup.
          </li>
          <li>
            <strong>Billing data:</strong> payment transactions are processed by ThriveCart. We do not
            store card numbers or full payment details — we only receive a record that a payment was made,
            the associated email address, and a ThriveCart customer reference.
          </li>
          <li>
            <strong>Usage data:</strong> information about how you use the Service, including log data,
            IP addresses, browser type, pages visited, and actions taken within the platform. This is
            collected automatically for security and service-improvement purposes.
          </li>
          <li>
            <strong>Configuration data:</strong> API credentials and settings you enter into the platform
            (e.g. SMTP configuration, Twilio credentials). These are stored encrypted and used solely to
            operate the Service on your behalf.
          </li>
          <li>
            <strong>Prospect data you upload:</strong> names, email addresses, phone numbers, and other
            contact details of your prospects. You are the data controller for this data; we process it as
            your data processor under a controller-processor relationship. See Section 7.
          </li>
        </ul>
      </Section>

      <Section title="2. How We Use Your Data">
        <p>We use your personal data for the following purposes:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>To create and manage your account and provide access to the Service;</li>
          <li>To process your subscription and communicate billing-related information;</li>
          <li>To send transactional emails (account activation, password resets, billing receipts);</li>
          <li>To monitor and improve the security and performance of the Service;</li>
          <li>To comply with our legal obligations;</li>
          <li>To respond to your support enquiries.</li>
        </ul>
        <p>
          We do not use your data for advertising, profiling, or any purpose unrelated to operating the Service.
        </p>
      </Section>

      <Section title="3. Legal Basis for Processing (UK GDPR)">
        <p>We rely on the following legal bases:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>Contract (Article 6(1)(b)):</strong> processing your account and billing data is
            necessary to fulfil your subscription agreement with us.
          </li>
          <li>
            <strong>Legitimate interests (Article 6(1)(f)):</strong> we process usage and log data to
            maintain the security, reliability, and integrity of the Service.
          </li>
          <li>
            <strong>Legal obligation (Article 6(1)(c)):</strong> we retain certain data as required by
            applicable law (e.g. financial records).
          </li>
        </ul>
      </Section>

      <Section title="4. Third Parties We Share Data With">
        <p>
          We share personal data only with service providers necessary to operate the Service. All processors
          are bound by data processing agreements:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>ThriveCart</strong> — payment processing. Handles billing and subscription management.
            Subject to their own Privacy Policy.
          </li>
          <li>
            <strong>Twilio</strong> — SMS delivery. Processes phone numbers included in SMS campaigns
            you create. Twilio is a US-based provider operating under Standard Contractual Clauses.
          </li>
          <li>
            <strong>Resend / SendGrid</strong> — transactional and campaign email delivery. Processes
            email addresses used in outreach campaigns and system emails.
          </li>
          <li>
            <strong>Hunter.io</strong> — email verification. Email addresses submitted for verification
            are sent to Hunter.io's API for deliverability checking.
          </li>
          <li>
            <strong>DigitalOcean</strong> — cloud infrastructure hosting. All data is stored on servers
            located in the European Union (Frankfurt).
          </li>
        </ul>
        <p>
          We do not sell personal data to third parties or share it for marketing purposes.
        </p>
      </Section>

      <Section title="5. International Data Transfers">
        <p>
          Some of our third-party providers (Twilio, ThriveCart, Hunter.io) process data outside the UK
          and European Economic Area. Where this occurs, we ensure appropriate safeguards are in place,
          such as the International Data Transfer Agreement (IDTA) or Standard Contractual Clauses (SCCs),
          as required under UK GDPR.
        </p>
      </Section>

      <Section title="6. Data Retention">
        <p>We retain personal data for as long as necessary for the purposes described:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>Account data:</strong> retained for the duration of your subscription and for up to
            30 days after account closure, to allow for data export requests.
          </li>
          <li>
            <strong>Billing records:</strong> retained for 7 years to comply with UK financial record-keeping
            requirements.
          </li>
          <li>
            <strong>Usage / log data:</strong> retained for up to 90 days for security monitoring purposes.
          </li>
          <li>
            <strong>Prospect data you upload:</strong> retained while your account is active. Deleted
            within 30 days of account closure unless you export it beforehand.
          </li>
        </ul>
      </Section>

      <Section title="7. Your Prospects' Data — Controller vs Processor">
        <p>
          When you upload prospect data (names, emails, phone numbers) and use it to run campaigns, you are
          the <strong>data controller</strong> for that data and we are your <strong>data processor</strong>.
          This means:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>You are responsible for ensuring you have a lawful basis to contact your prospects;</li>
          <li>You are responsible for honouring unsubscribe requests and maintaining your own DNC list;</li>
          <li>We only process prospect data on your documented instructions (operating the Service);</li>
          <li>We will not use prospect data for any purpose of our own.</li>
        </ul>
        <p>
          If a prospect contacts you with a data rights request (e.g. right to erasure), you must handle it
          directly. You can delete prospect records from within the platform at any time.
        </p>
      </Section>

      <Section title="8. Your Rights Under UK GDPR">
        <p>As a data subject, you have the following rights regarding your own account data:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>Right of access</strong> — request a copy of the data we hold about you;</li>
          <li><strong>Right to rectification</strong> — ask us to correct inaccurate data;</li>
          <li><strong>Right to erasure</strong> — request deletion of your personal data (subject to legal retention obligations);</li>
          <li><strong>Right to restriction</strong> — ask us to restrict processing in certain circumstances;</li>
          <li><strong>Right to data portability</strong> — receive your data in a structured, machine-readable format;</li>
          <li><strong>Right to object</strong> — object to processing based on legitimate interests.</li>
        </ul>
        <p>
          To exercise any of these rights, contact us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-indigo-600 hover:underline">{CONTACT_EMAIL}</a>.
          We will respond within 30 days. There is no charge for reasonable requests.
        </p>
        <p>
          You also have the right to lodge a complaint with the UK's data protection authority,
          the{' '}
          <a
            href="https://ico.org.uk"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 hover:underline"
          >
            Information Commissioner's Office (ICO)
          </a>
          , if you believe we have not handled your data lawfully.
        </p>
      </Section>

      <Section title="9. Cookies">
        <p>
          Outreach Engine uses only a single session cookie to keep you logged in. This cookie is strictly
          necessary for the Service to function and does not track you across other websites.
          We do not use advertising cookies, analytics cookies, or any third-party tracking pixels on
          this website.
        </p>
      </Section>

      <Section title="10. Security">
        <p>
          We implement appropriate technical and organisational measures to protect your personal data,
          including encrypted connections (HTTPS/TLS), hashed password storage, and access controls.
          However, no system is completely secure and we cannot guarantee absolute security. In the event
          of a data breach that is likely to pose a risk to your rights and freedoms, we will notify you
          and the ICO as required by law.
        </p>
      </Section>

      <Section title="11. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. We will notify you by email at least 14 days
          before any material changes take effect. The current version will always be available at this page.
        </p>
      </Section>

      <Section title="12. Contact Us">
        <p>
          For any privacy-related questions, requests, or concerns, please contact us at:
        </p>
        <p>
          <strong>{COMPANY}</strong><br />
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-indigo-600 hover:underline">{CONTACT_EMAIL}</a>
        </p>
      </Section>
    </main>

    {/* FOOTER */}
    <footer className="bg-gray-950 border-t border-gray-800 py-8 px-6">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
            <Zap size={14} className="text-white" />
          </div>
          <span className="text-white font-bold text-sm">Outreach Engine</span>
        </Link>
        <div className="flex items-center gap-8">
          <Link to="/" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">Home</Link>
          <Link to="/pricing" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">Pricing</Link>
          <Link to="/terms" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">Terms</Link>
          <Link to="/privacy" className="text-gray-400 hover:text-gray-300 text-sm transition-colors">Privacy</Link>
        </div>
        <p className="text-gray-600 text-sm">© {new Date().getFullYear()} {COMPANY}</p>
      </div>
    </footer>
  </div>
);

export default PrivacyPage;
